import React from "react";
import { formatValue } from "@/lib/api";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import DmeTag from "@/components/DmeTag";
import FormatBadge from "@/components/FormatBadge";
import { POS_COLORS, ordinal, Stat } from "./shared";

export default function OverviewTab({ ov, settings, bannerDismissed, settingsBusy, dismissBanner, excludeEmpty, restoreAll, toggleLeague }) {
  return (
    <div className="space-y-5" data-testid="ptab-content-overview">
      {!bannerDismissed && (ov.leagues || []).filter(l => l.value === 0 && l.included).length > 0 && (
        <div className="card-bordered p-4 border-l-2 border-l-[var(--accent)] flex flex-wrap items-center justify-between gap-3" data-testid="empty-banner">
          <div className="text-sm text-[var(--text-2)]">
            <span className="text-white font-semibold">{(ov.leagues || []).filter(l => l.value === 0 && l.included).length} empty league(s)</span> are inflating your exposure view. Hide them for a cleaner read?
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => { excludeEmpty(); dismissBanner(); }} className="btn-primary text-xs" data-testid="banner-hide">Hide them</button>
            <button onClick={dismissBanner} className="btn-secondary text-xs" data-testid="banner-dismiss">Dismiss</button>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label="LEAGUES" value={ov.total_leagues} sub={`${ov.accounts || 1} Sleeper account${(ov.accounts || 1) !== 1 ? "s" : ""}`} />
        <Stat label="TOTAL VALUE" dme value={formatValue(ov.total_value)} sub="across all rosters" />
        <Stat label="UNIQUE ASSETS" value={ov.exposure.length} sub={`incl. ${(ov.picks || []).length} pick${(ov.picks || []).length !== 1 ? "s" : ""}`} />
      </div>

      <div className="card-bordered p-5">
        <div className="text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono mb-4">POSITION CONCENTRATION</div>
        {(() => {
          const entries = Object.entries(ov.position_concentration).filter(([k, v]) => ["QB", "RB", "WR", "TE", "PICK"].includes(k) && v > 0);
          const max = Math.max(1, ...entries.map(([, v]) => v));
          return entries.sort((a, b) => b[1] - a[1]).map(([pos, val]) => (
            <div key={pos} className="flex items-center gap-3 mb-2.5" data-testid={`pos-conc-${pos}`}>
              <div className="w-8 text-xs font-mono" style={{ color: POS_COLORS[pos] }}>{pos}</div>
              <div className="flex-1 h-5 bg-black/40 rounded-sm overflow-hidden">
                <div className="h-full rounded-sm" style={{ width: `${val / max * 100}%`, background: POS_COLORS[pos], opacity: 0.85 }} />
              </div>
              <div className="w-20 text-right font-mono text-sm">{formatValue(val)}</div>
            </div>
          ));
        })()}
      </div>

      {(ov.picks || []).length > 0 && (
        <div className="card-bordered p-5" data-testid="draft-capital">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono">DRAFT CAPITAL · {(ov.picks || []).length} PICKS</div>
            <div className="font-mono text-sm text-[#60A5FA]">{formatValue(ov.picks_total || 0)}</div>
          </div>

          {ov.draft_capital && (ov.draft_capital.leagues || []).length > 0 && (() => {
            const dc = ov.draft_capital;
            const pct = dc.vs_avg_pct || 0;
            const above = pct >= 0;
            return (
              <div className="mb-4 p-4 rounded-sm bg-black/30 border border-[var(--border)]" data-testid="draft-capital-vs-avg">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono">VS LEAGUE AVERAGE</div>
                    <div className="text-sm text-[var(--text-2)] mt-1">
                      Your draft capital is{" "}
                      <span className={`font-semibold ${above ? "text-[var(--accent)]" : "text-[var(--amber)]"}`} data-testid="dc-vs-avg-pct">
                        {above ? "+" : ""}{pct}% {above ? "above" : "below"}
                      </span>{" "}
                      the average team across your leagues.
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-lg text-[#60A5FA]">{formatValue(dc.my_total)}</div>
                    <div className="text-[10px] tracking-widest text-[var(--text-3)]">AVG {formatValue(dc.avg_total)}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {dc.leagues.map((l) => (
                    <div key={l.league_id} className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-black/40 border border-[var(--border)]" data-testid={`dc-rank-${l.league_id}`}>
                      <span className="text-[var(--text-3)] truncate max-w-[120px]">{l.name}</span>
                      <span className={`font-mono font-semibold ${l.rank <= Math.ceil(l.teams / 3) ? "text-[var(--accent)]" : l.rank > l.teams - Math.ceil(l.teams / 3) ? "text-[var(--amber)]" : "text-white"}`}>
                        {ordinal(l.rank)}/{l.teams}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="space-y-1">
            {(ov.picks || []).map((p, i) => (
              <div key={`${p.league_id}-${p.name}-${i}`} className="flex items-center justify-between text-sm py-2 border-b border-[var(--border)] last:border-0" data-testid={`pick-row-${i}`}>
                <div className="min-w-0">
                  <div className="truncate">{p.name}</div>
                  <div className="text-[10px] tracking-widest text-[var(--text-3)]">{p.league_name}</div>
                </div>
                <div className="font-mono text-[#60A5FA] shrink-0">{formatValue(p.value)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-bordered p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] tracking-[0.2em] text-[var(--text-3)] font-mono flex items-center gap-2">
            LEAGUES · COUNTED IN EXPOSURE
            {settingsBusy && <Loader2 className="w-3 h-3 spin text-[var(--accent)]" data-testid="pf-settings-busy" />}
          </div>
          <div className="flex items-center gap-3">
            {(ov.leagues || []).some(l => !l.included) && (
              <button onClick={restoreAll} disabled={settingsBusy} className="text-[10px] text-[var(--text-2)] hover:text-[var(--accent)] inline-flex items-center gap-1 disabled:opacity-50" data-testid="restore-all-btn">
                <Eye className="w-3 h-3" /> Restore {(ov.leagues || []).filter(l => !l.included).length} excluded
              </button>
            )}
            {(ov.leagues || []).some(l => l.value === 0 && l.included) && (
              <button onClick={excludeEmpty} disabled={settingsBusy} className="text-[10px] text-[var(--text-2)] hover:text-[var(--accent)] inline-flex items-center gap-1 disabled:opacity-50" data-testid="exclude-empty-btn">
                <EyeOff className="w-3 h-3" /> Exclude empty leagues
              </button>
            )}
          </div>
        </div>
        <div className={`space-y-1 transition-opacity ${settingsBusy ? "opacity-60" : ""}`}>
          {ov.leagues.map(l => (
            <div key={l.league_id} className={`flex items-center justify-between text-sm py-2 border-b border-[var(--border)] last:border-0 ${l.included ? "" : "opacity-45"}`} data-testid={`pf-league-${l.league_id}`}>
              <div className="flex items-center gap-2 min-w-0">
                <button onClick={() => toggleLeague(l)} disabled={settingsBusy} data-testid={`league-toggle-${l.league_id}`}
                  className={`shrink-0 w-9 h-5 rounded-full relative transition-colors disabled:cursor-wait ${l.included ? "bg-[var(--accent)]" : "bg-[var(--border)]"}`} title={l.included ? "Counted — click to exclude" : "Excluded — click to include"}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-black transition-all ${l.included ? "left-[18px]" : "left-0.5"}`} />
                </button>
                <span className="truncate">{l.name}</span>
                <FormatBadge format={l.league_format || l.format} />
                {!l.included && <span className="text-[9px] tracking-widest text-[var(--text-3)] uppercase">excluded</span>}
              </div>
              <div className="font-mono text-[var(--accent)] shrink-0">{formatValue(l.value)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
